import { Device } from "@capacitor/device";
import { useEffect, useRef, useState } from "react";
import { util, type DeviceType } from "~/utils/util";
import { callApi } from "~/utils/common/hooks/axiosHelper";
import type { EmployeeDetails, LinkedDevicesCheckInput } from "~/core/Login/loginSchema";
import { Effect, pipe } from "effect";
import useSWR from "swr";
import { useNavigate } from "react-router";
import { constants } from "~/utils/constants";
import { url } from "~/utils/constants/lib/url";

 const getValidSession = (arg: { body: LinkedDevicesCheckInput }) => {
  return Effect.tryPromise({
    try: () => {
      return callApi({
        apiClientKey: "help",
        method: "post",
        url: `/employee/auth/validate-linked-device`,
        data: arg.body
      }).then((res) => res.data);
    },
    catch: (error: any) => new Error(util.handleApiError(error).message),
  });
};

const getFrappeMe = () => {
  return Effect.tryPromise({
    try: () =>
      callApi({
        apiClientKey: "help",
        method: "get",
        url: `/employee/auth/me`,
      }).then((res) => res.data),
    catch: (error: any) => new Error(util.handleApiError(error).message),
  });
};


export default function PostLogin() {
  const navigate = useNavigate();
  const [payload, setPayload] =
    useState<LinkedDevicesCheckInput | null>(null);

  const isBrowser = typeof window !== "undefined";
  const token = isBrowser ? localStorage.getItem("token") : null;
  const frappeAuthType = isBrowser ? localStorage.getItem("frappeAuthType") : null;
  const isNewFrappeFlow = frappeAuthType === "qr" || frappeAuthType === "email";
  const pathname = isBrowser ? window.location.pathname : "";
  const skipped =
    !token ||
    ["callback", "logout"].some((p) => pathname.includes(p));

  // Snapshot the token used when the current validation cycle started.
  // If it differs from localStorage at handler time, another tab wrote a
  // fresh login while our request was in flight — bail so we don't kick that
  // tab to /login by clearing shared state.
  const requestTokenRef = useRef<string | null>(null);

  const loginResultHandlers: any = {
    REDIRECT_LOGIN: () => {
      if (isBrowser) {
        const currentToken = localStorage.getItem("token");
        if (currentToken && currentToken !== requestTokenRef.current) return;
      }
      util.clearCookie("filter");
      navigate(url.GROW_URL.EMPLOYEE_LOGIN);
    },

    LOGIN_REQUEST_PENDING: (data: { employee: { team_lead?: string } }) => {
      navigate(
        `${url.GROW_URL.EMPLOYEE_LOGIN}/login-request-pending?tl=${data?.employee?.team_lead ?? ""}`
      );
    },
    organization: (data: { employee: EmployeeDetails }) => {
      const employee = data?.employee;
      if (employee) {
        localStorage.setItem(
          constants.EMPLOYEE_SESSION,
          JSON.stringify(employee)
        );
      }
      const currentPathname = isBrowser ? window.location.pathname : "";
      const alreadyThere = currentPathname.startsWith(constants.GROW_URL.EMPLOYEE_ORGANIZATION);
      if (!alreadyThere) {
        navigate(constants.GROW_URL.EMPLOYEE_ORGANIZATION, { replace: true });
      }
    },
    LANDING_PAGE: (data: { employee: EmployeeDetails }) => {
      const employee = data?.employee;
      if (employee) {
        localStorage.setItem(
          constants.EMPLOYEE_SESSION,
          JSON.stringify(employee)
        );
      }
      const landingPage: Record<string, string> = constants.LANDING_PAGE_ROUTES
        const path = landingPage[employee.grow_landing_page] || constants.GROW_URL.EMPLOYEE_INDENT;
      const currentPathname = window.location.pathname;
      const isLogin = currentPathname.includes("login") || currentPathname === '/';

      const redirectPathname = isLogin ? path : currentPathname;

      navigate(redirectPathname);
    },
  } as const;

  useEffect(() => {
    if (skipped) {
      // Logout / callback / logout-path: clear the payload so SWR unmounts
      // the fetch and the handler effect won't fire with stale cached data
      // when the user logs back in on the same layout instance.
      setPayload(null);
      return;
    }

    // New Frappe flow (email/QR): no linked_device row exists — the attendance
    // check is served by /employee/auth/me, driven purely by the OIDC bearer
    // token. Signal SWR to run the /me branch by setting a marker payload.
    if (isNewFrappeFlow) {
      requestTokenRef.current = localStorage.getItem("token");
      setPayload({ deviceTypeId: 0, deviceId: "__frappe_me__", userId: null });
      return;
    }

    const submitSessionValidation = async () => {
      try {
        const deviceTypeUserAgent: DeviceType = util.getDeviceTypeFromUserAgent(navigator.userAgent)
        const deviceTypeId =
          util.deviceTypeToCodeMap[deviceTypeUserAgent];

        const deviceInfo = await Device.getId();

        const sessionPayload: LinkedDevicesCheckInput = {
          deviceTypeId,
          deviceId: deviceInfo.identifier,
          userId: null,
        };

        requestTokenRef.current = localStorage.getItem("token");
        setPayload(sessionPayload);
      } catch (error) {
        console.warn("Failed to get device ID", error);
      }
    };

    submitSessionValidation();
    // `token` in the deps ensures a fresh login (post-logout / re-login on the
    // same layout instance) re-runs the validation. SWR handles duplicate
    // requests via its cache — same key → cached data, so no thrashing.
  }, [skipped, token, isNewFrappeFlow]);

  const { data, error } = useSWR(
    payload && !skipped ? [isNewFrappeFlow ? "postLoginMe" : "postLogin", payload] : null,
    () =>
      pipe(
        isNewFrappeFlow ? getFrappeMe() : getValidSession({ body: payload! }),
        Effect.runPromise
      ),
      {
        refreshInterval: (latestData) =>latestData?._tag === "LOGIN_REQUEST_PENDING" ? 30000: 0
      }
  );


  useEffect(() => {
    if (skipped) return;
    const tag = data?._tag;
    if (!tag) return;
    loginResultHandlers[tag]?.(data);
    // SWR caches by key; a stable payload → stable `data` reference → this
    // effect fires exactly once per response. A fresh login (token changes)
    // resets the payload state via the effect above, which sends SWR through
    // a fresh fetch and delivers a new `data` reference here — that's how a
    // second login on the same mounted layout still gets routed.
  }, [data, skipped]);

  useEffect(() => {
    if (skipped) return;
    if (error) {
      console.error("Session validation error:", error);
    }
  }, [error, skipped]);

  return null
}
